import { Tabs } from '@base-ui/react/tabs';
import styled from '@emotion/styled';

import {
  DISABLED_EXCLUSIONS,
  DISABLED_SELECTORS,
  focusRing,
  hoverable,
} from '../../interaction-states';
import { radius } from '../../radius';
import { forHover } from '../../responsive';
import { scrollbarStyles } from '../../scrollbar';
import { shadows } from '../../shadows';
import { typographyVariantStyles } from '../../typography-variants';
import type { TabsOrientation, TabsSize, TabsVariant } from './types';

// ---------------------------------------------------------------------------
// Size maps
// ---------------------------------------------------------------------------

const tabSizeStyles: Record<TabsSize, object> = {
  sm: {
    padding: '0 8px',
    gap: 6,
    height: 28,
    ...typographyVariantStyles.body3,
    fontWeight: 500,
  },
  md: {
    padding: '0 12px',
    gap: 6,
    height: 30,
    ...typographyVariantStyles.body3,
    fontWeight: 500,
  },
  lg: {
    padding: '0 12px',
    gap: 8,
    height: 32,
    ...typographyVariantStyles.body2,
    fontWeight: 500,
  },
};

const iconSizeStyles: Record<TabsSize, { width: number; height: number }> = {
  sm: { width: 16, height: 16 },
  md: { width: 16, height: 16 },
  lg: { width: 20, height: 20 },
};

const boxedListPaddingBySize: Record<TabsSize, number> = {
  sm: radius[2],
  md: radius[2],
  lg: 4,
};

// ---------------------------------------------------------------------------
// StyledTabList
// ---------------------------------------------------------------------------

/**
 * A flex or grid parent sizes an unconstrained child to its max-content width, so the
 * root needs these to stay inside its container before the scroller can take effect.
 */
export const StyledTabsRoot = styled(Tabs.Root, {
  shouldForwardProp: (prop) => prop !== '$fullWidth',
})<{ $fullWidth: boolean }>(({ $fullWidth }) => ({
  maxWidth: '100%',
  minWidth: 0,
  width: $fullWidth ? '100%' : undefined,
}));

/**
 * Owns the horizontal scroll instead of the list itself: the bordered indicator sits
 * 1px below the list padding box, so scrolling on the list would make it vertically
 * scrollable by that pixel and let the active underline drift out of view.
 */
const TAB_LIST_TRANSIENT_PROPS = new Set(['$size', '$variant', '$fullWidth', '$orientation']);

export const StyledTabListScroller = styled('div', {
  shouldForwardProp: (prop) => !TAB_LIST_TRANSIENT_PROPS.has(prop),
})<{ $fullWidth: boolean; $orientation: TabsOrientation }>(({ $fullWidth, $orientation }) => ({
  maxWidth: '100%',
  width: $fullWidth ? '100%' : undefined,
  overflowX: $orientation === 'horizontal' ? 'auto' : undefined,
  ...scrollbarStyles,
}));

export const StyledTabList = styled(Tabs.List, {
  shouldForwardProp: (prop) => !TAB_LIST_TRANSIENT_PROPS.has(prop),
})<{
  $size: TabsSize;
  $variant: TabsVariant;
  $fullWidth: boolean;
  $orientation: TabsOrientation;
}>(({ theme, $size, $variant, $fullWidth, $orientation }) => {
  const isVertical = $orientation === 'vertical';

  return {
    display: $fullWidth ? 'flex' : 'inline-flex',
    flexDirection: isVertical ? 'column' : 'row',
    width: $fullWidth ? '100%' : undefined,
    alignItems: isVertical ? 'stretch' : 'center',
    gap: isVertical ? theme.spacing[8] : undefined,

    ...($variant === 'boxed'
      ? {
          padding: boxedListPaddingBySize[$size],
          borderRadius: radius[8],
          backgroundColor: theme.colors.background.raised,
        }
      : {
          position: 'relative',
          ...(isVertical
            ? { borderRight: `1px solid ${theme.colors.border.default}` }
            : { borderBottom: `1px solid ${theme.colors.border.default}` }),
        }),
  };
});

// The band overlaps the list's hairline by 1px so the active colour replaces it.
const activeLineBand = (orientation: TabsOrientation) =>
  ({
    position: 'absolute',
    borderRadius: radius.full,
    ...(orientation === 'vertical' ? { right: -1, width: 2 } : { bottom: -1, height: 2 }),
  }) as const;

export const StyledIndicator = styled(Tabs.Indicator, {
  shouldForwardProp: (prop) => prop !== '$orientation',
})<{ $orientation: TabsOrientation }>(({ theme, $orientation }) => ({
  ...activeLineBand($orientation),
  backgroundColor: theme.colors.brand.solid,

  ...($orientation === 'vertical'
    ? {
        top: 'var(--active-tab-top)',
        height: 'var(--active-tab-height)',
        transition:
          'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }
    : {
        left: 'var(--active-tab-left)',
        width: 'var(--active-tab-width)',
        transition:
          'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }),

  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}));

// ---------------------------------------------------------------------------
// StyledTab
// ---------------------------------------------------------------------------

export const StyledTab = styled(Tabs.Tab, {
  shouldForwardProp: (prop) => !TAB_LIST_TRANSIENT_PROPS.has(prop),
})<{
  $size: TabsSize;
  $variant: TabsVariant;
  $fullWidth: boolean;
  $orientation: TabsOrientation;
}>(({ theme, $size, $variant, $fullWidth, $orientation }) => {
  const size = tabSizeStyles[$size];
  const isVertical = $orientation === 'vertical';

  const boxedStateStyles = {
    borderRadius: radius[8],

    ...hoverable(theme, ['surfaceStrong', 'text'], '&:not([data-active])'),
    '&[data-active]': {
      backgroundColor: theme.colors.background.overlay,
      borderRadius: radius[6],
      boxShadow: shadows.sm,
    },
    ...focusRing(theme),
  };

  const borderedStateStyles = {
    position: 'relative' as const,
    justifyContent: isVertical ? 'flex-start' : 'center',

    '&::after': {
      ...activeLineBand($orientation),
      content: '""',
      ...(isVertical ? { top: 0, bottom: 0 } : { left: 0, right: 0 }),
      backgroundColor: 'transparent',
      transition: theme.motion.hover,
    },

    [forHover]: {
      [`&:hover${DISABLED_EXCLUSIONS}::after`]: {
        backgroundColor: theme.colors.border.strong,
      },
    },
    ...focusRing(theme),
  };

  return {
    // Layout
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    ...($fullWidth ? { flex: '1 1 0', minWidth: 0, justifyContent: 'center' } : {}),

    // Size
    ...size,

    // Reset
    border: 'none',
    background: 'transparent',
    outline: 'none',

    // Appearance
    color: theme.colors.text.primary,
    cursor: 'pointer',
    whiteSpace: 'nowrap',

    // Transitions
    transition: theme.motion.hover,

    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::after': { transition: 'none' },
    },

    ...($variant === 'boxed' ? boxedStateStyles : borderedStateStyles),

    // Disabled
    [DISABLED_SELECTORS]: {
      opacity: 0.5,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  };
});

// ---------------------------------------------------------------------------
// StyledIcon
// ---------------------------------------------------------------------------

export const StyledIcon = styled('span', {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: TabsSize }>(({ $size }) => {
  const size = iconSizeStyles[$size];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: size.width,
    height: size.height,

    '& svg': {
      width: '100%',
      height: '100%',
    },
  };
});

// ---------------------------------------------------------------------------
// StyledCounter
// ---------------------------------------------------------------------------

export const StyledCounter = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.background.overlay,
  borderRadius: radius.full,
  padding: '2px 8px',
  color: theme.colors.text.primary,
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  flexShrink: 0,
}));

// ---------------------------------------------------------------------------
// StyledPanel
// ---------------------------------------------------------------------------

export const StyledPanel = styled(Tabs.Panel)({});
